import React, { useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { UserStore } from '../store/userStore'
import Card from '../components/Card'
import { usePortal } from '../hooks/usePortal'
import { createPortal } from 'react-dom'
import { ERROR_DICT, errorDict } from '../utils/errorDict'
import TextField from '../components/TextField'
import { NAME_VALIDATION_PATTERN } from '../constants'
import Btn from '../components/Btn'
import { axiosInstance } from '../hooks/useAxios'
import { toast } from 'react-toastify'
import Breadcrumbs from '../components/Breadcrumbs'
import ImageUpload from '../components/ImageUpload'
import addArticle from '../api/addArticle'

const BREADCRUMBS = [
  {
    label: 'Главная',
    path: '/'
  },
  {
    label: 'Новости',
    path: '/news'
  },
  {
    label: 'Публикация новости',
    path: '/news/new'
  }
]

function NewsNewPage(): React.ReactElement {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<any>({
    defaultValues: {
      name: UserStore.data.name,
      secondName: UserStore.data.secondName,
      lastName: UserStore.data.lastName,
      newsName: '',
      newsDate: '',
      newsText: '',
      file: null
    }
  })
  const formRef = useRef<HTMLFormElement>(null)

  const { portal } = usePortal()
  const [loading, setLoading] = React.useState(false)
  const [resetFile, setResetFile] = React.useState(false)

  const onSubmit = async (data: any): Promise<void> => {
    const formData = new FormData()

    Object.entries(data).forEach(([key, value]) => {
      if (key === 'file') {
        formData.append('file', value as File)
      } else {
        if (typeof value === 'object') {
          formData.append(key, JSON.stringify(value))
        } else {
          formData.append(key, value + '')
        }
      }
    })

    try {
      setLoading(true)
      await axiosInstance(addArticle('/news/item', formData))
      toast.success('Новость успешно создана')
      reset()
      setResetFile(true)
      const dateField = formRef.current?.querySelector('input[name="newsDate"]') as HTMLInputElement;
      if (dateField instanceof HTMLInputElement) {
        dateField.value = ''
      }
    } catch (error: any) {
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="page">
      <Breadcrumbs className="page__breadcrumbs" breadcrumbs={BREADCRUMBS} />
      {portal &&
        createPortal(<Breadcrumbs breadcrumbs={BREADCRUMBS} />, portal)}

      <header className="page__head">
        <h1>Публикация новости</h1>
      </header>
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <header className={'card-head text-h3'}>Автор новости</header>

          <div className={'card-content'}>
            <div className={'row row-gap-20 row-mobile-none'}>
              <TextField
                disabled
                className={'u-flexGrow1'}
                control={control}
                rules={{
                  required: true,
                  pattern: {
                    value: NAME_VALIDATION_PATTERN,
                    message: ''
                  }
                }}
                hasError={!!errors.lastName}
                errorText={errorDict(errors.lastName)}
                name="lastName"
                label="Фамилия"
                required
              ></TextField>
              <TextField
                disabled
                className={'u-flexGrow1'}
                control={control}
                rules={{
                  required: true,
                  pattern: {
                    value: NAME_VALIDATION_PATTERN,
                    message: ''
                  }
                }}
                hasError={!!errors.name}
                errorText={errorDict(errors.name)}
                name="name"
                label="Имя"
                required
              ></TextField>
              <TextField
                disabled
                className={'u-flexGrow1'}
                control={control}
                rules={{
                  required: true,
                  pattern: {
                    value: NAME_VALIDATION_PATTERN,
                    message: ''
                  }
                }}
                hasError={!!errors.secondName}
                errorText={errorDict(errors.secondName)}
                name="secondName"
                label="Отчество"
                required
              ></TextField>
            </div>
          </div>
        </Card>
        <Card className={'mt-md-5 mt-sm-4'}>
          <header className={'card-head text-h3'}>Новости</header>

          <div className={'card-content'}>
            <TextField
              className={'u-flexGrow1'}
              control={control}
              rules={{
                required: true
              }}
              hasError={!!errors.newsName}
              errorText={
                errors.newsName?.type === 'pattern'
                  ? ERROR_DICT.required
                  : errorDict(errors.newsName)
              }
              name="newsName"
              type="text"
              label="Заголовок новости"
              required
            ></TextField>

            <TextField
              className={'u-flexGrow1'}
              control={control}
              rules={{
                required: true,
                validate: (value, formValues) => {
                  return !!value || !!formValues.present
                }
              }}
              hasError={!!errors.newsDate}
              errorText={ERROR_DICT.required}
              datepickerOptions={{
                view: 'days',
                minView: 'days',
                dateFormat: 'dd.MM.yyyy'
              }}
              type="date"
              name="newsDate"
              label="Дата публикации"
              required
            ></TextField>

            <TextField
              helpText={'Максимум 6000 символов'}
              textarea
              label={'Текст новости'}
              rules={{ maxLength: 6000 }}
              hasError={!!errors.newsText}
              errorText={errorDict(errors.newsText)}
              name={'newsText'}
              control={control}
            ></TextField>
          </div>
        </Card>

        <Card className={'mt-md-5 mt-sm-4'}>
          <header className={'card-head text-h3'}>Обложка новости</header>

          <div className={'card-content'}>
            <Controller
              control={control}
              name={'file'}
              rules={{ required: false }}
              render={({ field: { value, onChange } }) => (
                <ImageUpload file={value} onChangeFile={onChange} resetFile={resetFile} />
              )}
            ></Controller>
          </div>
        </Card>
        <Btn
          className={'mt-md-8 mt-sm-6'}
          disabled={loading}
          loading={loading}
          type={'submit'}
          onClick={() => {
            setTimeout(() => {
              const errorEl = formRef.current?.querySelector('._has-error')

              errorEl?.scrollIntoView({ behavior: 'smooth' })
            })
          }}
        >
          Создать новость
        </Btn>
      </form>
    </div>
  )
}

export default NewsNewPage
